import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from 'apollo-react/components/Box';
import Modal from 'apollo-react/components/Modal';
import Typography from 'apollo-react/components/Typography';
import Button from 'apollo-react/components/Button';
import CircularProgress from 'apollo-react/components/CircularProgress';
import { toast } from 'react-toastify';
import profileStyles from '../../customerProfile/profileUi/Profile.module.css';
import buttonStyles from '../../../components/common/Button.module.css';
import {
  yupValidationErrorToFieldMap,
  pickStringApiFieldErrors,
} from '../utils/yupValidationErrors';

/** Editing key sentinel for create mode (aligned with legacy profile tabs). */
export const REPEATABLE_SECTION_NEW = 'new';

function normalizeMinMax(minEntries, maxEntries) {
  const min =
    typeof minEntries === 'number' &&
    Number.isFinite(minEntries) &&
    minEntries >= 0
      ? minEntries
      : 0;

  let max = Number.POSITIVE_INFINITY;
  if (maxEntries != null && maxEntries !== '') {
    const m = Number(maxEntries);
    if (Number.isFinite(m) && m >= 0) max = m;
  }

  if (max < min && max !== Number.POSITIVE_INFINITY) max = min;
  return { min, max };
}

/**
 * Repeatable CRUD shell: one editor at a time, optional delete modal, synced form array local state from `items`.
 *
 * Backend sync: pass `validateDraft`, `saveDraft`, and `deleteItem` calling your REST client; call `onSaved` to reload `/me`.
 *
 * @template TItem,TDraft
 * @param {{
 *   items: TItem[];
 *   getRowId: (item: TItem) => string|number;
 *   minEntries?: number;
 *   maxEntries?: number|null;
 *   locked?: boolean;
 *   lockNotice?: React.ReactNode;
 *   title: React.ReactNode;
 *   titleVariant?: 'section' | 'subsection';
 *   addButtonLabel?: string;
 *   emptyMessage?: string;
 *   editorTitleNew?: string;
 *   editorTitleEdit?: string;
 *   createEmptyDraft: () => TDraft;
 *   rowToDraft: (item: TItem) => TDraft;
 *   validateDraft?: (draft: TDraft, ctx: { isNew: boolean, editingKey: string|number }) => void | Promise<void>;
 *   saveDraft: (draft: TDraft, ctx: { isNew: boolean, editingId: string|number|null }) => void | Promise<void>;
 *   deleteItem: (rowId: string|number) => void | Promise<void>;
 *   deleteModalTitle?: string;
 *   getDeleteConfirmLabel?: (item: TItem) => string;
 *   renderEditor: (args: {
 *     draft: TDraft;
 *     updateDraft: (key: string, value: unknown) => void;
 *     isNew: boolean;
 *     editingId: string|number|null;
 *     rows: TItem[];
 *     saving: boolean;
 *     fieldErrors: Record<string, string>;
 *   }) => React.ReactNode;
 *   renderRow: (args: {
 *     item: TItem;
 *     index: number;
 *     canDelete: boolean;
 *     globalLock: boolean;
 *     deleteLocked: boolean;
 *     onEdit: () => void;
 *     onRequestDelete: () => void;
 *   }) => React.ReactNode;
 *   onSaved?: () => void | Promise<void>;
 * }} props
 */
export default function RepeatableSectionList({
  items,
  getRowId,
  minEntries = 0,
  maxEntries = null,
  locked = false,
  crossLockNotice = null,
  finishEditingHint = 'Finish or cancel the current edit before editing another row or adding a new one.',
  title,
  titleVariant = 'section',
  addButtonLabel = 'Add',
  emptyMessage = 'No entries.',
  editorTitleNew = 'New entry',
  editorTitleEdit = 'Edit entry',
  createEmptyDraft,
  rowToDraft,
  validateDraft,
  saveDraft,
  deleteItem,
  deleteModalTitle = 'Remove entry',
  getDeleteConfirmLabel,
  renderEditor,
  renderRow,
  onSaved,
  onLockStateChange,
  /** When false, the header "Add …" control is not rendered (e.g. admin snapshot review). */
  showAddButton = true,
}) {
  const { min: minN, max: maxN } = useMemo(
    () => normalizeMinMax(minEntries, maxEntries),
    [minEntries, maxEntries]
  );

  const [rows, setRows] = useState([]);
  useEffect(() => {
    setRows(Array.isArray(items) ? items : []);
  }, [items]);

  /** @type {[null|string|number, Function]} */
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState(null);
  /** @type {[null|string|number, Function]} */
  const [busy, setBusy] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editorFieldErrors, setEditorFieldErrors] = useState({});

  useEffect(() => {
    const active = editingKey != null || deleteTarget != null || busy != null;
    onLockStateChange?.(active);
    return () => onLockStateChange?.(false);
  }, [editingKey, deleteTarget, busy, onLockStateChange]);

  const isNew = editingKey === REPEATABLE_SECTION_NEW;
  const editingId =
    editingKey != null && editingKey !== REPEATABLE_SECTION_NEW
      ? editingKey
      : null;
  const globalLock = editingKey != null || locked;

  const canAddMore = rows.length < maxN && !locked;
  const canDeleteRow = rows.length > minN;

  const beginAdd = useCallback(() => {
    setEditorFieldErrors({});
    setDraft(createEmptyDraft());
    setEditingKey(REPEATABLE_SECTION_NEW);
  }, [createEmptyDraft]);

  const beginEdit = useCallback(
    (row) => {
      setEditorFieldErrors({});
      setDraft(rowToDraft(row));
      setEditingKey(getRowId(row));
    },
    [getRowId, rowToDraft]
  );

  const cancelEdit = useCallback(() => {
    setEditorFieldErrors({});
    setDraft(null);
    setEditingKey(null);
  }, []);

  const updateDraft = useCallback((k, v) => {
    setEditorFieldErrors((prev) => {
      if (prev == null || !Object.prototype.hasOwnProperty.call(prev, k))
        return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
    setDraft((p) => (p ? { ...p, [k]: v } : p));
  }, []);

  const saving =
    busy === REPEATABLE_SECTION_NEW ||
    (editingId != null && busy === editingId);

  const handleSave = async () => {
    if (!draft) return;
    try {
      if (validateDraft) {
        await validateDraft(draft, { isNew, editingKey });
      }
      const ctxId = isNew ? null : editingKey;
      const busyKey = isNew ? REPEATABLE_SECTION_NEW : editingKey;
      setBusy(busyKey);
      await saveDraft(draft, { isNew, editingId: ctxId });
      cancelEdit();
      await Promise.resolve(onSaved?.());
    } catch (e) {
      if (e?.name === 'ValidationError') {
        setEditorFieldErrors(yupValidationErrorToFieldMap(e));
        const first =
          (e.inner && e.inner[0] && e.inner[0].message) ||
          e.message ||
          'Validation failed';
        toast.error(String(first));
        return;
      }
      const errs = e.response?.data?.errors;
      const apiMap = pickStringApiFieldErrors(errs);
      if (Object.keys(apiMap).length) {
        setEditorFieldErrors((prev) => ({ ...prev, ...apiMap }));
      }
      const msg =
        (errs &&
          typeof errs === 'object' &&
          Object.values(errs).find((x) => typeof x === 'string')) ||
        e.response?.data?.message ||
        e.message ||
        'Save failed';
      toast.error(typeof msg === 'string' ? msg : 'Save failed');
    } finally {
      setBusy(null);
    }
  };

  const confirmDelete = async () => {
    if (deleteTarget == null) return;
    const id = deleteTarget.id;
    setBusy(`del-${id}`);
    try {
      await deleteItem(id);
      setDeleteTarget(null);
      if (editingKey === id) cancelEdit();
      await Promise.resolve(onSaved?.());
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Delete failed');
    } finally {
      setBusy(null);
    }
  };

  const deleteBusy =
    typeof busy === 'string' && busy.startsWith('del-') && deleteTarget
      ? busy === `del-${deleteTarget.id}`
      : false;

  const maxHint =
    maxN !== Number.POSITIVE_INFINITY ? (
      <Typography
        variant="bodySmall"
        color="text.secondary"
        component="span"
        sx={{ ml: 1 }}
      >
        {rows.length}/{maxN} maximum
      </Typography>
    ) : null;

  const minHint =
    minN > 0 ? (
      <Typography
        variant="bodySmall"
        color="text.secondary"
        component="span"
        sx={{ ml: 1 }}
      >
        Minimum {minN}
      </Typography>
    ) : null;

  const titleTypography = (
    <Typography
      variant={titleVariant === 'subsection' ? 'subtitle1' : 'h5'}
      component="div"
      sx={{
        mb: 0,
        ...(titleVariant === 'subsection' ? { color: 'text.secondary' } : {}),
      }}
    >
      {title}
      {maxHint}
      {minHint}
    </Typography>
  );

  return (
    <div>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={2}
      >
        {titleTypography}
        {showAddButton ? (
          <Button
            variant="secondary"
            size="small"
            className={buttonStyles.button}
            disabled={!canAddMore || globalLock}
            onClick={beginAdd}
          >
            {addButtonLabel}
          </Button>
        ) : null}
      </Box>

      {(editingKey != null || locked) && (
        <Box sx={{ color: 'text.secondary', fontSize: '0.875rem', mb: 2 }}>
          {editingKey != null && finishEditingHint ? (
            <Typography variant="bodySmall" component="p" sx={{ mb: 1 }}>
              {finishEditingHint}
            </Typography>
          ) : null}
          {locked && crossLockNotice ? (
            <Box sx={{ mb: editingKey != null ? 1 : 0 }}>{crossLockNotice}</Box>
          ) : null}
        </Box>
      )}
      {!globalLock && !canAddMore && maxN !== Number.POSITIVE_INFINITY ? (
        <Typography variant="bodySmall" color="text.secondary" sx={{ mb: 0 }}>
          Maximum number of entries reached.
        </Typography>
      ) : null}

      {editingKey === REPEATABLE_SECTION_NEW && draft ? (
        <div className={`${profileStyles.editorSurface} mb-3`}>
          <Box
            display="flex"
            alignItems="flex-start"
            mb={2}
            gap={2}
            flexWrap="wrap"
          >
            <div className={profileStyles.header}>{editorTitleNew}</div>
          </Box>
          {renderEditor({
            draft,
            updateDraft,
            isNew: true,
            editingId: null,
            rows,
            saving,
            fieldErrors: editorFieldErrors,
          })}
          <Box
            display="flex"
            gap={1}
            flexWrap="wrap"
            alignItems="center"
            mt={2}
          >
            <Button
              variant="secondary"
              status="danger"
              size="small"
              className={buttonStyles.button}
              onClick={cancelEdit}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="small"
              className={buttonStyles.button}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <CircularProgress size={18} style={{ marginRight: 8 }} />
                  Saving…
                </>
              ) : (
                'Save'
              )}
            </Button>
          </Box>
        </div>
      ) : null}

      {rows.map((item, index) => {
        const id = getRowId(item);
        const isEditingThis = editingKey === id;
        return (
          <React.Fragment key={String(id)}>
            {isEditingThis && draft ? (
              <div className={`${profileStyles.editorSurfaceMuted} mb-3`}>
                <Box
                  display="flex"
                  alignItems="flex-start"
                  mb={2}
                  gap={2}
                  flexWrap="wrap"
                >
                  <div className={profileStyles.header}>{editorTitleEdit}</div>
                </Box>
                {renderEditor({
                  draft,
                  updateDraft,
                  isNew: false,
                  editingId: id,
                  rows,
                  saving,
                  fieldErrors: editorFieldErrors,
                })}
                <Box
                  display="flex"
                  gap={1}
                  flexWrap="wrap"
                  alignItems="center"
                  mt={2}
                >
                  <Button
                    variant="secondary"
                    status="danger"
                    size="small"
                    className={buttonStyles.button}
                    onClick={cancelEdit}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="small"
                    className={buttonStyles.button}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <CircularProgress
                          size={18}
                          style={{ marginRight: 8 }}
                        />
                        Saving…
                      </>
                    ) : (
                      'Save'
                    )}
                  </Button>
                </Box>
              </div>
            ) : (
              renderRow({
                item,
                index,
                canDelete: canDeleteRow,
                globalLock,
                deleteLocked: Boolean(deleteTarget),
                onEdit: () => beginEdit(item),
                onRequestDelete: () =>
                  setDeleteTarget({
                    id,
                    label: getDeleteConfirmLabel
                      ? getDeleteConfirmLabel(item)
                      : String(id),
                  }),
              })
            )}
          </React.Fragment>
        );
      })}

      {!rows.length && editingKey !== REPEATABLE_SECTION_NEW ? (
        <Typography variant="bodySmall" color="text.secondary">
          {emptyMessage}
        </Typography>
      ) : null}

      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (deleteBusy) return;
          setDeleteTarget(null);
        }}
        title={deleteModalTitle}
        hideButtons
      >
        <Typography variant="body2">
          Remove this entry
          {deleteTarget?.label != null && deleteTarget.label !== ''
            ? ` (${deleteTarget.label})`
            : ''}
          ? This cannot be undone.
        </Typography>
        <Box
          display="flex"
          justifyContent="flex-end"
          flexWrap="wrap"
          gap={1}
          mt={2}
        >
          <Button
            variant="secondary"
            size="small"
            className={buttonStyles.button}
            onClick={() => {
              if (deleteBusy) return;
              setDeleteTarget(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="small"
            className={buttonStyles.button}
            disabled={!deleteTarget || deleteBusy}
            onClick={confirmDelete}
          >
            {deleteBusy ? (
              <>
                <CircularProgress size={18} style={{ marginRight: 8 }} />
                Deleting…
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </Box>
      </Modal>
    </div>
  );
}
